import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { RiderLayout } from "@/components/rider/RiderLayout";
import { MobileEventSelectorV2 } from "@/components/rider/MobileEventSelectorV2";
import { DesktopEventSelectorV2 } from "@/components/rider/desktop/DesktopEventSelectorV2";
import { LoadingEvents } from "@/components/rider/LoadingEvents";
import { VehicleSelector } from "@/components/rider/VehicleSelector";
import { CommunityEvent } from "@/types/rider";
import { getEventById } from "@/services/riderService";

// Vehicle options will be dynamically set based on event data
// Default fallback if API doesn't provide vehicle options
const defaultVehicleOptions = [
  {
    id: "party-bus",
    name: "Party Bus",
    capacity: 20,
    pricing: { "4": 1000, "7": 1750 } as Record<string, number>,
    icon: "🚌"
  }
];

const RiderEventSelectXandraHalloween = () => {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState(defaultVehicleOptions);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const [isImmersive, setIsImmersive] = useState(false);
  const [eventImage, setEventImage] = useState<string>("/xandra-halloween-og.png");

  // Xandra Halloween event ID from the database
  const XANDRA_HALLOWEEN_EVENT_ID = "3711ac11-816d-4a96-809a-b467f10bc27c";

  // Helper function to convert relative image paths to absolute URLs
  const getAbsoluteImageUrl = (imagePath: string) => {
    if (imagePath.startsWith('http')) {
      return imagePath; // Already absolute
    }
    return `${window.location.origin}${imagePath}`;
  };

  useEffect(() => {
    const loadEventData = async () => {
      try {
        console.log("Loading Xandra Halloween event with ID:", XANDRA_HALLOWEEN_EVENT_ID);
        const eventData = await getEventById(XANDRA_HALLOWEEN_EVENT_ID);
        
        if (eventData && eventData.pricingOptions) {
          console.log("Received event data with pricing options:", eventData);
          
          // Use the actual pricing options from the API
          const pricingOptions = eventData.pricingOptions;
          
          // Create event variants based on actual pricing options
          const eventVariants: CommunityEvent[] = pricingOptions.map((option, index) => ({
            ...eventData,
            id: `${eventData.id}-${option.duration_hours}hour-${option.is_round_trip ? 'roundtrip' : 'oneway'}`,
            startTime: `${option.duration_hours} Hour Reservation | ${option.is_round_trip ? 'Round Trip' : 'One Way'}`,
            price: option.rider_price
          }));
          
          setEvents(eventVariants);
          
          // Create vehicle options with correct pricing based on actual durations from API
          const pricing: Record<string, number> = {};
          pricingOptions.forEach(opt => {
            pricing[opt.duration_hours.toString()] = opt.rider_price;
          });
          
          const eventVehicleOptions = [
            {
              id: "party-bus",
              name: "Party Bus",
              capacity: pricingOptions[0].passenger_count || 20,
              pricing: pricing,
              icon: "🚌"
            }
          ];
          
          setVehicleOptions(eventVehicleOptions);
          
          // Set the event image for meta tags
          if (eventData.image) {
            setEventImage(eventData.image);
          }
        } else {
          console.log("Using fallback pricing data");
          // Fallback to default pricing structure
          const fallbackEvent = eventData || {
            id: XANDRA_HALLOWEEN_EVENT_ID,
            name: "Xandra Halloween",
            location: "Xandra Nightclub",
            region: "Las Vegas",
            image: "/Default_Party_Bus.jpeg", // Use a working local image as fallback
            date: "2025-10-31",
            startTime: "9:00 PM",
            price: 1000
          };
          
          const eventVariants: CommunityEvent[] = [
            {
              ...fallbackEvent,
              id: `${fallbackEvent.id}-4hour-oneway`,
              startTime: "4 Hour Reservation | One Way",
              price: 1200
            },
            {
              ...fallbackEvent,
              id: `${fallbackEvent.id}-7hour-roundtrip`,
              startTime: "7 Hour Reservation | Round Trip", 
              price: 1750
            }
          ];
          
          setEvents(eventVariants);
          
          // Set vehicle options with default pricing: $1200 for 4-hour, $1750 for 7-hour
          const eventVehicleOptions = [
            {
              id: "party-bus",
              name: "Party Bus",
              capacity: 20,
              pricing: { "4": 1200, "7": 1750 } as Record<string, number>,
              icon: "🚌"
            }
          ];
          
          setVehicleOptions(eventVehicleOptions);
        }
      } catch (error) {
        console.error("Failed to load Xandra Halloween event:", error);
        const fallbackEvents: CommunityEvent[] = [
          {
            id: `${XANDRA_HALLOWEEN_EVENT_ID}-4hour-oneway`,
            name: "Xandra Halloween",
            location: "Xandra Nightclub",
            region: "Las Vegas",
            image: "/Default_Party_Bus.jpeg", // Use a working local image as fallback
            date: "2025-10-31",
            startTime: "4 Hour Reservation | One Way",
            price: 1200
          },
          {
            id: `${XANDRA_HALLOWEEN_EVENT_ID}-7hour-roundtrip`,
            name: "Xandra Halloween",
            location: "Xandra Nightclub", 
            region: "Las Vegas",
            image: "/Default_Party_Bus.jpeg", // Use a working local image as fallback
            date: "2025-10-31",
            startTime: "7 Hour Reservation | Round Trip",
            price: 1750
          }
        ];
        
        setEvents(fallbackEvents);
        
        // Fallback vehicle options with correct pricing
        const fallbackVehicleOptions = [
          {
            id: "party-bus",
            name: "Party Bus",
            capacity: 20,
            pricing: { "4": 1200, "7": 1750 } as Record<string, number>,
            icon: "🚌"
          }
        ];
        
        setVehicleOptions(fallbackVehicleOptions);
      } finally {
        setLoading(false);
      }
    };

    loadEventData();
  }, []);

  const handleEventSelection = (eventId: string, index: number) => {
    setSelectedEventId(eventId || null);
    setActiveIndex(index);
  };

  const handleResetSelection = () => {
    setSelectedEventId(null);
    setSelectedVehicleId(null);
    setActiveIndex(0);
    setIsImmersive(false);
  };

  const handleVehicleSelection = (vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
  };

  const handleBackToEvents = () => {
    setSelectedEventId(null);
    setSelectedVehicleId(null);
    setIsImmersive(false);
  };

  const handleBackToVehicles = () => {
    setSelectedVehicleId(null);
  };

  const handleContinue = (selectedAddOns?: any[], totalPrice?: number) => {
    if (selectedEventId && selectedVehicleId) {
      const selectedEvent = events.find(event => event.id === selectedEventId);
      const selectedVehicle = vehicleOptions.find(vehicle => vehicle.id === selectedVehicleId);
      const isRoundTrip = selectedEvent?.startTime?.includes('Round Trip') ? 'true' : 'false';
      const venueAddress = selectedEvent?.location || "Xandra Nightclub, Las Vegas, NV";
      
      const params = new URLSearchParams({ 
        eventId: XANDRA_HALLOWEEN_EVENT_ID, // Use original event ID for API calls
        capacity: selectedVehicle?.capacity.toString() || '20',
        isRoundTrip: isRoundTrip,
        destination: venueAddress,
        vehicleType: selectedVehicle?.name || ''
      });
      
      // For round trips, handle return trip information
      if (isRoundTrip === 'true') {
        // Check if after-party add-on is selected for return destination
        const afterPartyAddon = selectedAddOns?.find(addon => 
          addon.description.includes('after-party') || addon.description.includes('after party')
        );
        
        if (afterPartyAddon) {
          // Set appropriate after-party destinations for Xandra Halloween events
          params.append('returnDestination', 'Las Vegas Strip, NV');
          params.append('returnPickup', venueAddress);
        } else {
          params.append('returnDestination', '');
          params.append('returnPickup', venueAddress);
        }
      }
      
      if (totalPrice) {
        params.append('price', totalPrice.toString());
      }
      
      // Create notes with vehicle information
      let notes = `${selectedVehicle?.name} - Capacity ${selectedVehicle?.capacity}`;
      
      if (selectedAddOns && selectedAddOns.length > 0) {
        params.append('addOns', JSON.stringify(selectedAddOns));
        
        const addOnNotes = selectedAddOns
          .map(addon => `${addon.description} (+$${addon.price})`)
          .join('\n');
        notes += '\n' + addOnNotes;
      }
      
      params.append('notes', notes);
      
      navigate(`/rider-quote?${params.toString()}`);
    }
  };

  const handleManualEntry = () => {
    handleResetSelection();
  };

  const handlePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
      setSelectedEventId(events[activeIndex - 1].id);
    }
  };

  const handleNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
      setSelectedEventId(events[activeIndex + 1].id);
    }
  };

  // Mobile-specific navigation handlers that only change activeIndex
  const handleMobilePrevCard = () => {
    if (activeIndex > 0) {
      setActiveIndex(prev => prev - 1);
    }
  };

  const handleMobileNextCard = () => {
    if (activeIndex < events.length - 1) {
      setActiveIndex(prev => prev + 1);
    }
  };

  const handleCardTap = (eventId: string, index: number) => {
    if (!isImmersive) {
      setIsImmersive(true);
    }
    handleEventSelection(eventId, index);
  };

  return (
    <RiderLayout>
      <Helmet>
        <title>Xandra Halloween 2025 | Disco Party Bus</title>
        <meta name="description" content="Book your party bus for Xandra's Halloween 2025 event - featuring DJ ORRA" />
        <meta property="og:title" content="Xandra Halloween 2025 | Disco Party Bus" />
        <meta property="og:description" content="Book your party bus for Xandra's Halloween 2025 event - featuring DJ ORRA" />
        <meta property="og:image" content={getAbsoluteImageUrl(eventImage)} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={`${window.location.origin}/xandra-halloween`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={getAbsoluteImageUrl(eventImage)} />
      </Helmet>
      <div className="min-h-screen bg-white transition-colors duration-300">
        {loading ? (
          <LoadingEvents />
        ) : (
          <>
            <MobileEventSelectorV2
              events={events}
              selectedEventId={selectedEventId}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              activeIndex={activeIndex}
              onEventSelect={handleEventSelection}
              onVehicleSelect={handleVehicleSelection}
              onContinue={handleContinue}
              onManualEntry={handleManualEntry}
              onPrevCard={handleMobilePrevCard}
              onNextCard={handleMobileNextCard}
              onBackToVehicles={handleBackToVehicles}
            />

            <DesktopEventSelectorV2
              events={events}
              activeIndex={activeIndex}
              selectedEventId={selectedEventId}
              selectedVehicleId={selectedVehicleId}
              vehicleOptions={vehicleOptions}
              isImmersive={isImmersive}
              setIsImmersive={setIsImmersive}
              handleEventSelection={handleEventSelection}
              handlePrevCard={handlePrevCard}
              handleNextCard={handleNextCard}
              handleCardTap={handleCardTap}
              handleContinue={handleContinue}
              handleManualEntry={handleManualEntry}
              handleResetSelection={handleResetSelection}
              handleVehicleSelection={handleVehicleSelection}
              handleBackToVehicles={handleBackToVehicles}
            />
          </>
        )}
      </div>
    </RiderLayout>
  );
};

export default RiderEventSelectXandraHalloween;
